import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import PercentIcon from '@mui/icons-material/Percent'
import TourOutlinedIcon from '@mui/icons-material/TourOutlined'
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import React from 'react'
import {
  Box,
  Collapse,
  List,
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import WorkIcon from '@mui/icons-material/Work'
import BusinessIcon from '@mui/icons-material/Business'
import TuneIcon from '@mui/icons-material/Tune'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import PaletteIcon from '@mui/icons-material/Palette'
import StorageIcon from '@mui/icons-material/Storage'
import MemoryIcon from '@mui/icons-material/Memory'
import SearchIcon from '@mui/icons-material/Search'
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import TimelineIcon from '@mui/icons-material/Timeline'
import QueueIcon from '@mui/icons-material/Queue'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import NotificationsIcon from '@mui/icons-material/Notifications'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import LockIcon from '@mui/icons-material/Lock'
import TerminalIcon from '@mui/icons-material/Terminal'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import ExtensionIcon from '@mui/icons-material/Extension'
import LinkIcon from '@mui/icons-material/Link'
import SpeedIcon from '@mui/icons-material/Speed'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import PsychologyIcon from '@mui/icons-material/Psychology'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import CableOutlinedIcon from '@mui/icons-material/CableOutlined'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'

interface NavItem {
  route: string
  label: string
  icon: React.ReactElement
  permission: 'all' | 'isManager' | 'isRealmAdmin' | 'isRealmAdminOrTeamManager' | 'isManagerOrStatsReviewer' | 'isManagerOrGuideReviewer'
  /**
   * Not listed in the drawer, but still KNOWN to it — so isRouteVisible keeps
   * applying `permission` to a pasted URL. Removing an entry would shut its
   * page to everyone, Managers included: unknown routes are refused (T12).
   */
  hidden?: boolean
  /**
   * The route params the item navigates with. Admin pages take the customer
   * (the default); a few items the menu took over from the old three-dots
   * pane (30 Sep 2026) are app pages: AutoCoder needs the job too, and My
   * subscriptions takes neither. A 'job' item is not listed while no job is
   * selected.
   */
  scope?: 'customer' | 'job' | 'none'
  /** Extra params (a programme tracker's slug). */
  params?: Record<string, string>
}

/** A programme tracker configured for the customer (useCustomerTrackers). */
export interface AdminDrawerTracker {
  slug: string
  title: string
}

interface NavGroup {
  label: string
  items: NavItem[]
  /**
   * Collapsed by default (24 Sep call, #1523 item 11 — Adam: "you only see
   * the top level thing and then you click it"). Only Management uses this;
   * every other group renders open, as before.
   */
  collapsible?: boolean
}

// 24 Sep call regroup (#1523 item 11). Customer-facing groups first, the
// Manager-only catch-all last and collapsed. Four routes that used to be
// isRealmAdmin (admin-cache, admin-llm-usage, admin-autocoder-throughput,
// admin-models) move to isManager here, and Notifications drops from 'all'
// to isManager — Management is a Manager-only group throughout, which is
// what keeps a Realm user's drawer to exactly the customer-facing items
// (acceptance: PR1 plan §"PR1 — Nav regroup + Realm visibility").
// 12 Oct (Matt's order): People, Programme trackers, Configuration (Alerts
// first), AI, Management, Account. A Manager's per-tracker view links sit in
// Programme trackers, not Management.
// Exported for help/features coverage: every item must be in a System
// context section (featureCoverage.test.ts).
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'People',
    items: [
      { route: 'admin-users', label: 'User management', icon: <PeopleIcon />, permission: 'isRealmAdmin' },
      // Guided conversations (28 Oct 2026, Adam): Managers write guides,
      // invite people and read the responses; Realm admins do neither.
      // Manager only, on the server too.
      { route: 'admin-guides', label: 'Guided conversations', icon: <QuestionAnswerOutlinedIcon />, permission: 'isManagerOrGuideReviewer' },
      // Not in the drawer: reached from the guides list. Still KNOWN, so a
      // pasted URL gets the Manager gate.
      { route: 'admin-guide', label: 'Guided conversations', icon: <QuestionAnswerOutlinedIcon />, permission: 'isManagerOrGuideReviewer', hidden: true },
      { route: 'admin-guide-agenda', label: 'Guide agenda', icon: <QuestionAnswerOutlinedIcon />, permission: 'isManagerOrGuideReviewer', hidden: true },
    ],
  },
  {
    label: 'Programme trackers',
    items: [
      // Was Manager-only under Configuration; Realm admins run their own
      // customer's tracker (#1523 item 12 — see programme_tracker_admin.go).
      { route: 'admin-programme-trackers', label: 'Manage trackers', icon: <TrackChangesIcon />, permission: 'isRealmAdmin' },
      // Not in the drawer — reached only from the trackers list — but still
      // KNOWN, so a pasted URL still gets the isRealmAdmin gate (#1263).
      { route: 'admin-programme-tracker-edit', label: 'Programme tracker', icon: <TrackChangesIcon />, permission: 'isRealmAdmin', hidden: true },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { route: 'alerts', label: 'Alerts', icon: <NotificationsActiveIcon />, permission: 'all' },
      // B2: everyone reads the skills library and edits their own; the API
      // gates Company-level and public changes per row.
      { route: 'admin-agent-skills', label: 'Skills', icon: <ExtensionIcon />, permission: 'all' },
      { route: 'admin-context', label: 'Context', icon: <TuneIcon />, permission: 'all' },
      // #1523: everyone reads the personas library and makes their own; the
      // API gates Company-level changes to Managers and Realm admins per row.
      { route: 'admin-personas', label: 'Personas', icon: <RecordVoiceOverIcon />, permission: 'all' },
      // #1266: three-tier ownership — Managers and Realm users edit
      // company-level themes (the API enforces per row), so both see the page.
      { route: 'admin-brand-themes', label: 'Brand Themes', icon: <PaletteIcon />, permission: 'isRealmAdmin' },
    ],
  },
  {
    label: 'Social',
    items: [
      // Every member reads the social posts a job holds and their coded
      // comments (the AutoCoder data reads are open to members); only a
      // Manager adds post links, and the page shows that button disabled
      // with the reason to everyone else.
      { route: 'admin-social-posts', label: 'Social posts', icon: <ForumOutlinedIcon />, permission: 'all' },
    ],
  },
  {
    label: 'AI',
    items: [
      // Rename, archive and view budget teams (#1563). Realm admins read it;
      // the writes are Manager-only on the server.
      { route: 'admin-teams', label: 'Teams', icon: <GroupsOutlinedIcon />, permission: 'isRealmAdminOrTeamManager' },
      // Matt, 7 Oct: client-safe AI group. Usage is the credits-only face of
      // AI budgets that a Realm admin or a Manager can show a customer; AI
      // budgets and AI usage moved to Management (Manager only).
      { route: 'admin-usage', label: 'Usage', icon: <ReceiptLongIcon />, permission: 'isRealmAdmin' },
      // Not in the drawer: reached from the Usage landing. Listed so
      // isRouteVisible knows it (closed by default).
      { route: 'admin-usage-team', label: 'Usage', icon: <ReceiptLongIcon />, permission: 'isRealmAdmin', hidden: true },
      // #1652: every flagged advanced analysis, for the customer's named stats
      // reviewer (/me stats_reviewer) and any Manager.
      { route: 'admin-stats-review', label: 'Stats review', icon: <FactCheckOutlinedIcon />, permission: 'isManagerOrStatsReviewer' },
      // #1234 (Sky SKY-08/09/20/23): every model in use, where it runs, its
      // model card, and the Changes list. Realm admins read; Managers write.
      { route: 'admin-ai-models', label: 'AI models in use', icon: <MemoryOutlinedIcon />, permission: 'isRealmAdmin' },
    ],
  },
  {
    label: 'Management',
    collapsible: true,
    items: [
      // From the old three-dots pane (Matt + Kai, 30 Sep 2026): the AutoCoder
      // pipeline admin and My subscriptions are Manager-only, on the server
      // too (access audit T11).
      { route: 'autocoder', label: 'AutoCoder', icon: <AutoAwesomeMotionIcon />, permission: 'isManager', scope: 'job' },
      { route: 'my-subscriptions', label: 'My subscriptions', icon: <NotificationsActiveIcon />, permission: 'isManager', scope: 'none' },
      // Moved here from the AI group (Matt, 7 Oct): they show our cost, markup
      // and margin, so they are Manager only.
      { route: 'admin-ai-budgets', label: 'AI budgets', icon: <SavingsOutlinedIcon />, permission: 'isManager' },
      { route: 'admin-ai-budget-team', label: 'AI budgets', icon: <SavingsOutlinedIcon />, permission: 'isManager', hidden: true },
      { route: 'admin-ai-usage', label: 'AI usage', icon: <ReceiptLongIcon />, permission: 'isManager' },
      // Replaces DNA's New Customer and licence editing (design/2026-10-14-dna-replacement-admin.md). Manager only, server too.
      { route: 'admin-customers', label: 'Customers', icon: <BusinessIcon />, permission: 'isManager' },
      { route: 'admin-jobs', label: 'Jobs', icon: <WorkIcon />, permission: 'isManager' },
      // SPSS import (Adam and Matt, 20 Oct; #1914): off the Variables page,
      // which now opens on the data view. An app page, Manager only on the
      // route and the server (job_sav_import.go).
      { route: 'sav-import', label: 'Import variables', icon: <UploadFileIcon />, permission: 'isManager', scope: 'customer' },
      // The construct tools page (tree, Edit Vartree, New construct, the
      // Variables helper) has no home until its UI pass (#1889); until then
      // a Manager reaches it here (Adam, 20 Oct). The route itself stays
      // open to members, as before.
      { route: 'variables-tools', label: 'Variable tools', icon: <AccountTreeOutlinedIcon />, permission: 'isManager', scope: 'job' },
      // Data connections (#1863): Manager only, on the server too (Adam, 16 Oct).
      { route: 'admin-data-connections', label: 'Data connections', icon: <CableOutlinedIcon />, permission: 'isManager' },
      { route: 'admin-data-connection', label: 'Data connection', icon: <CableOutlinedIcon />, permission: 'isManager', hidden: true },
      // Social accounts: followed and connected accounts the first-party social import reads. Manager only, on the server too.
      { route: 'admin-social-accounts', label: 'Social accounts', icon: <ShareOutlinedIcon />, permission: 'isManager' },
      { route: 'admin-prompts', label: 'Prompts', icon: <AutoFixHighIcon />, permission: 'isManager' },
      // System context (13 Oct): a Manager's note per feature of the
      // registry (help/features), each section's opening line and starter
      // questions, and the Help pane's tours. Moved here from Configuration
      // (Adam, 19 Oct). Manager only, on the server too.
      { route: 'admin-system-context', label: 'System context', icon: <MenuBookOutlinedIcon />, permission: 'isManager' },
      // Walkthroughs (20 Oct 2026): the click-through cards that drive the
      // real page from a file shipped with a feature. Manager only, route too.
      { route: 'admin-walkthroughs', label: 'Walkthroughs', icon: <SlideshowOutlinedIcon />, permission: 'isManager' },
      // Demos (design/2026-10-22-chief-of-staff-and-demos.md): mark a demo
      // customer, Reset its story, play its steps. Manager only, server too.
      { route: 'admin-demos', label: 'Demos', icon: <TheatersOutlinedIcon />, permission: 'isManager' },
      // The old Help & tours page, now System context's Tours tab: not in the
      // drawer, still KNOWN so the old address is gated (Layout redirects it).
      { route: 'admin-help-tours', label: 'Help & tours', icon: <TourOutlinedIcon />, permission: 'isManager', hidden: true },
      { route: 'admin-cache', label: 'Cache Management', icon: <StorageIcon />, permission: 'isManager' },
      { route: 'admin-carbon-cache', label: 'Carbon Cache', icon: <MemoryIcon />, permission: 'isManager' },
      { route: 'admin-search', label: 'Search Index', icon: <SearchIcon />, permission: 'isManager' },
      { route: 'admin-sql', label: 'SQL Query', icon: <TerminalIcon />, permission: 'isManager' },
      { route: 'admin-llm-usage', label: 'LLM Usage', icon: <ReceiptLongIcon />, permission: 'isManager' },
      // Fixed-mode billing (spend control PR 6c). The prices are what Bayes
      // Price sells and the margin is our cost: Manager only, on the server too.
      { route: 'admin-billing-price-list', label: 'Price list', icon: <SellOutlinedIcon />, permission: 'isManager' },
      { route: 'admin-billing-margin', label: 'Margin', icon: <PercentIcon />, permission: 'isManager' },
      // Try billing settings on invented usage; reads and writes nothing. Manager only, server too.
      { route: 'admin-billing-simulator', label: 'Billing simulator', icon: <ScienceOutlinedIcon />, permission: 'isManager' },
      // The retired Token Usage path — not in the drawer, still KNOWN.
      { route: 'admin-token-usage', label: 'LLM Usage', icon: <ReceiptLongIcon />, permission: 'isManager', hidden: true },
      { route: 'admin-autocoder-throughput', label: 'AutoCoder Throughput', icon: <SpeedIcon />, permission: 'isManager' },
      { route: 'admin-models', label: 'Models', icon: <PsychologyIcon />, permission: 'isManager' },
      { route: 'admin-activity', label: 'User Activity', icon: <TimelineIcon />, permission: 'isManager' },
      { route: 'admin-share-views', label: 'Dashboard Share Views', icon: <LinkIcon />, permission: 'isManager' },
      { route: 'admin-media-library', label: 'Media Library', icon: <PermMediaIcon />, permission: 'isManager' },
      { route: 'admin-tasks', label: 'Task Queue', icon: <QueueIcon />, permission: 'isManager' },
      { route: 'admin-azure-costs', label: 'Azure Costs', icon: <AttachMoneyIcon />, permission: 'isManager' },
      { route: 'admin-notifications', label: 'Notifications', icon: <NotificationsIcon />, permission: 'isManager' },
      { route: 'admin-sessions', label: 'Agent Sessions', icon: <SmartToyIcon />, permission: 'isManager' },
      // Hidden for now, Managers included (constants/agentFeatures).
      { route: 'admin-monitoring', label: 'Monitoring', icon: <MonitorHeartIcon />, permission: 'isManager' },
    ],
  },
  {
    label: 'Account',
    items: [
      { route: 'admin-password', label: 'Change Password', icon: <LockIcon />, permission: 'all' },
      // From the old three-dots pane (30 Sep 2026): every user's own pages.
      { route: 'ai-access', label: 'Your AI access', icon: <VerifiedUserOutlinedIcon />, permission: 'all' },
      // Agent runner plan M4: the notes Claude keeps about the signed-in
      // person at this customer. Their own only, read-only.
      { route: 'agent-memory', label: 'What Claude remembers', icon: <StickyNote2OutlinedIcon />, permission: 'all' },
    ],
  },
]

/** The Programme trackers group's per-tracker view links, one per tracker the
 *  customer has (the old three-dots pane's contextual link). Manager-only;
 *  the tracker page itself (`/projects/:slug`) is requireUser. */
function withTrackerItems(groups: NavGroup[], trackers: AdminDrawerTracker[]): NavGroup[] {
  if (trackers.length === 0) return groups
  const items: NavItem[] = trackers.map((tracker) => ({
    route: 'programme-tracker',
    label: trackers.length > 1 ? tracker.title : 'Programme tracker',
    icon: <TimelineIcon />,
    permission: 'isManager',
    scope: 'none',
    params: { slug: tracker.slug },
  }))
  return groups.map((group) => {
    if (group.label !== 'Programme trackers') return group
    return { ...group, items: [...group.items, ...items] }
  })
}

function navParams(item: NavItem, customer: string, job: string): Record<string, string> {
  const base: Record<string, string> =
    item.scope === 'none' ? {} : item.scope === 'job' ? { customer, job } : { customer }
  return { ...base, ...(item.params || {}) }
}

/** An item is the current page when its route matches and so does every
 *  param it pins (a tracker's slug). */
function isItemActive(item: NavItem, currentRoute: string, currentParams: Record<string, string>): boolean {
  if (item.route !== currentRoute) return false
  return Object.entries(item.params || {}).every(([k, v]) => currentParams[k] === v)
}

export const DRAWER_WIDTH = 240

interface AdminDrawerProps {
  currentRoute: string
  /** The params of the current route, so a tracker item knows it is active. */
  currentParams?: Record<string, string>
  /** Called with the item's route and its full params (see NavItem.scope). */
  onNavigate: (route: string, params: Record<string, string>) => void
  /** The selected customer and job the items navigate with. */
  customer?: string
  job?: string
  /** The customer's programme trackers (useCustomerTrackers). */
  trackers?: AdminDrawerTracker[]
  isManager: boolean
  isRealmAdmin: boolean
  /** Appointed over at least one budget team (/me `manages`). Opens the Teams page only. */
  isTeamManager?: boolean
  /** The caller is a stats reviewer of this customer (/me stats_reviewer, #1652). */
  isStatsReviewer?: boolean
  /** A Manager named the caller a reviewer of a guide (/me guide_reviewer, migration 000190). */
  isGuideReviewer?: boolean
  open: boolean
  onClose: () => void
  variant: 'permanent' | 'temporary'
  /** Which side the drawer sits on. The admin menu lives on the RIGHT since
   *  30 Sep 2026 (the place the old three-dots pane used); left stays the
   *  default for any host that does not say. */
  anchor?: 'left' | 'right'
}

function isItemVisible(permission: NavItem['permission'], isManager: boolean, isRealmAdmin: boolean, isTeamManager: boolean, isStatsReviewer: boolean, isGuideReviewer: boolean): boolean {
  if (permission === 'all') return true
  if (permission === 'isManager') return isManager
  if (permission === 'isRealmAdmin') return isRealmAdmin
  if (permission === 'isRealmAdminOrTeamManager') return isRealmAdmin || isTeamManager
  if (permission === 'isManagerOrStatsReviewer') return isManager || isStatsReviewer
  if (permission === 'isManagerOrGuideReviewer') return isManager || isGuideReviewer
  return false
}

/** Whether this user may open the admin route at all — the same gate the
 *  drawer uses to list it, so a pasted URL cannot reach a hidden page (#1263).
 *  CLOSED BY DEFAULT (access audit A07-10, T12): a route the drawer does not
 *  know is refused to everyone, so a new admin page added without a
 *  NAV_GROUPS entry is shut until someone decides who may see it —
 *  AdminDrawer.test.tsx checks every admin-* route in routes.tsx is listed. */
export function isRouteVisible(route: string, isManager: boolean, isRealmAdmin: boolean, isTeamManager = false, isStatsReviewer = false, isGuideReviewer = false): boolean {
  for (const group of NAV_GROUPS) {
    const item = group.items.find((i) => i.route === route)
    if (item) return isItemVisible(item.permission, isManager, isRealmAdmin, isTeamManager, isStatsReviewer, isGuideReviewer)
  }
  return false
}

/** The admin page to land on: the first admin-* item this user may open.
 *  App pages the menu also lists (AutoCoder, Alerts, …) are never a landing. */
export function getFirstVisibleRoute(isManager: boolean, isRealmAdmin: boolean, isTeamManager = false, isStatsReviewer = false, isGuideReviewer = false): string {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.route.startsWith('admin-') && isItemVisible(item.permission, isManager, isRealmAdmin, isTeamManager, isStatsReviewer, isGuideReviewer)) {
        return item.route
      }
    }
  }
  return 'admin-password'
}

const NO_TRACKERS: AdminDrawerTracker[] = []
const NO_PARAMS: Record<string, string> = {}

const AdminDrawer: React.FC<AdminDrawerProps> = ({
  currentRoute,
  currentParams = NO_PARAMS,
  onNavigate,
  customer = '',
  job = '',
  trackers = NO_TRACKERS,
  isManager,
  isRealmAdmin,
  isTeamManager = false,
  isStatsReviewer = false,
  isGuideReviewer = false,
  open,
  onClose,
  variant,
  anchor = 'left',
}) => {
  const groups = React.useMemo(() => withTrackerItems(NAV_GROUPS, trackers), [trackers])

  // Management collapses by default (24 Sep call, #1523 item 11 — Adam:
  // "you only see the top level thing and then you click it"), but opens
  // itself when the current route lives inside it, so the active item is
  // never hidden behind a closed group.
  const [managementOpen, setManagementOpen] = React.useState(() =>
    groups.some(
      (group) => group.collapsible && group.items.some((item) => item.route === currentRoute)
    )
  )

  const renderNavItem = (item: NavItem) => {
    const isActive = isItemActive(item, currentRoute, currentParams)
    return (
      <ListItemButton
        key={item.route + (item.params ? JSON.stringify(item.params) : '')}
        selected={isActive}
        onClick={() => {
          onNavigate(item.route, navParams(item, customer, job))
          if (variant === 'temporary') onClose()
        }}
        sx={{
          py: 0.75,
          pl: 2,
          borderLeft: isActive ? '3px solid' : '3px solid transparent',
          borderLeftColor: isActive ? 'info.main' : 'transparent',
          '&.Mui-selected': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(41, 182, 246, 0.08)'
                : 'rgba(2, 136, 209, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(41, 182, 246, 0.12)'
                : 'rgba(2, 136, 209, 0.12)',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: isActive ? 'info.main' : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'info.main' : 'text.primary',
          }}
        />
      </ListItemButton>
    )
  }

  const drawerContent = (
    <Box sx={{ overflow: 'auto', pt: 1 }}>
      {groups.map((group) => {
        const visibleItems = group.items.filter((item) =>
          !item.hidden &&
          !(item.scope === 'job' && !job) &&
          isItemVisible(item.permission, isManager, isRealmAdmin, isTeamManager, isStatsReviewer, isGuideReviewer)
        )
        if (visibleItems.length === 0) return null

        if (group.collapsible) {
          return (
            <Box key={group.label}>
              <ListItemButton onClick={() => setManagementOpen((v) => !v)} sx={{ py: 0.75, pl: 2 }}>
                <ListItemText
                  primary={group.label}
                  primaryTypographyProps={{
                    variant: 'overline',
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: 1.5,
                  }}
                />
                {managementOpen ? (
                  <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                )}
              </ListItemButton>
              <Collapse in={managementOpen} timeout="auto" unmountOnExit>
                <List disablePadding>{visibleItems.map(renderNavItem)}</List>
              </Collapse>
            </Box>
          )
        }

        return (
          <List
            key={group.label}
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  typography: 'overline',
                  color: 'text.secondary',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  lineHeight: '32px',
                  px: 2,
                }}
              >
                {group.label}
              </ListSubheader>
            }
            disablePadding
          >
            {visibleItems.map(renderNavItem)}
          </List>
        )
      })}
    </Box>
  )

  return (
    <Drawer
      variant={variant}
      anchor={anchor}
      open={variant === 'permanent' ? true : open}
      onClose={onClose}
      PaperProps={{ component: 'nav', 'aria-label': 'Admin menu' } as object}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          position: variant === 'permanent' ? 'relative' : 'fixed',
          backgroundColor: 'background.paper',
          // The hairline faces the page, whichever side the drawer is on.
          ...(anchor === 'right' ? { borderLeft: 1 } : { borderRight: 1 }),
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default AdminDrawer
